// LxSearchBox — Lamtix-token-wrapped search input
//
// MCP-extracted measurements (get_design_context, CustomersList frame):
//   minHeight: 44px (WCAG touch target)
//   borderRadius: 10px
//   padding: 11px 14px, paddingInlineStart: 36px (room for icon)
//   icon: insetInlineStart 12px, Search size 16, color --lx-neutral-500
//   rest bg: --lx-neutral-50
//   focus: border --lx-primary-500, ring 0 0 0 3px --lx-primary-200, bg #fff
//   fontVariantNumeric: tabular-nums lining-nums (D-NUMBERS-EN-01)
//
// D-SHADCN-LAMTIX-WRAPPED-01: sole import path is @lamtix/ui
// D-NUMBERS-EN-01: tabular-nums on numeric input (phone search)
// D-PHONE-INPUT-PATTERN-01: icon at insetInlineStart (RTL-aware)

import * as React from 'react';
import { Search } from 'lucide-react';

export type SearchBoxLocale = 'ar' | 'en';

export interface LxSearchBoxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  locale: SearchBoxLocale;
  /** Outer wrapper className */
  wrapperClassName?: string;
  /** Outer wrapper style — use to set flex:1 or fixed width */
  wrapperStyle?: React.CSSProperties;
}

const LxSearchBox = React.forwardRef<HTMLInputElement, LxSearchBoxProps>(
  ({ locale, wrapperClassName, wrapperStyle, style, onFocus, onBlur, ...props }, ref) => {
    const isAr = locale === 'ar';
    const [focused, setFocused] = React.useState(false);

    return (
      <div
        className={wrapperClassName}
        style={{ position: 'relative', minWidth: 240, ...wrapperStyle }}
      >
        <Search
          size={16}
          aria-hidden="true"
          style={{
            position: 'absolute',
            insetInlineStart: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--lx-neutral-500)',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
          }}
        />
        <input
          ref={ref}
          type="search"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '11px 14px',
            paddingInlineStart: 36,
            borderRadius: 10,
            border: `1px solid ${focused ? 'var(--lx-primary-500)' : 'var(--lx-neutral-200)'}`,
            background: focused ? '#fff' : 'var(--lx-neutral-50)',
            boxShadow: focused ? '0 0 0 3px var(--lx-primary-200)' : 'none',
            fontSize: 14,
            minHeight: 44,
            fontVariantNumeric: 'tabular-nums lining-nums',
            fontFamily: isAr ? 'var(--lx-font-arabic)' : 'var(--lx-font-latin)',
            color: 'var(--lx-neutral-800)',
            outline: 'none',
            transition: 'border-color 120ms ease, box-shadow 120ms ease, background 120ms ease',
            ...style,
          }}
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          {...props}
        />
      </div>
    );
  },
);
LxSearchBox.displayName = 'LxSearchBox';

export { LxSearchBox };
